/**
 * DashboardReport
 *
 * Renders a "dashboard" report: a 4-column grid of KPI cards.
 * Each KPI reads its rows from one of the report's queries (matched on
 * queryId in gridData) and is drawn according to its type:
 *   badge – single aggregated value
 *   table – compact table of rows
 *   bar   – bar chart (one or more y fields)
 *   line  – line chart (one or more y fields)
 *   area  – line chart with a filled area under each series
 *   pie   – pie chart with a percentage legend
 *
 * Charts are plain SVG laid out with d3 scales (no extra chart dependency).
 */

import { useEffect, useRef, useState } from "react"
import { arc as d3Arc, pie as d3Pie, scaleBand, scaleLinear } from "d3"

const SERIES_COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2"]

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return null
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""))
  return isNaN(n) ? null : n
}

const fmt = (v) => {
  const n = toNumber(v)
  if (n === null) return v === null || v === undefined ? "" : v
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

const compact = (n) => {
  const abs = Math.abs(n)
  if (abs >= 1e7) return (n / 1e7).toFixed(abs >= 1e8 ? 0 : 1).replace(/\.0$/, "") + "Cr"
  if (abs >= 1e5) return (n / 1e5).toFixed(abs >= 1e6 ? 0 : 1).replace(/\.0$/, "") + "L"
  if (abs >= 1e3) return (n / 1e3).toFixed(abs >= 1e4 ? 0 : 1).replace(/\.0$/, "") + "K"
  return String(n)
}

const prettyHeader = (key) => String(key).replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())

const fmtCell = (v) =>
  Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const aggregate = (rows, field, fn) => {
  if (fn === "count") return rows.length
  if (fn === "first") return rows[0] ? rows[0][field] : null
  const values = rows.map((r) => toNumber(r[field])).filter((n) => n !== null)
  if (values.length === 0) return null
  switch (fn) {
    case "avg":
      return values.reduce((a, b) => a + b, 0) / values.length
    case "min":
      return Math.min(...values)
    case "max":
      return Math.max(...values)
    default:
      return values.reduce((a, b) => a + b, 0)
  }
}

// ── KPI renderers ────────────────────────────────────────────────────────────

const BadgeKpi = ({ config, data }) => {
  const value = aggregate(data, config.valueField, config.aggregate || "sum")
  return (
    <div style={{ ...styles.badgeValue, color: config.color || "#2563eb" }}>
      {config.prefix || ""}
      {value === null || value === undefined ? "–" : fmt(value)}
      {config.suffix || ""}
    </div>
  )
}

const TableKpi = ({ config, data }) => {
  const configured = (config.columns || []).filter((c) => c.key)
  const columns = configured.length
    ? configured.map((c) => ({ key: c.key, header: c.header || c.key }))
    : Object.keys(data[0] || {}).map((k) => ({ key: k, header: prettyHeader(k) }))
  const rows = data.slice(0, config.maxRows || 10)

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={styles.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={styles.th}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {columns.map((c) => {
                const numeric = typeof row[c.key] === "number"
                return (
                  <td key={c.key} style={{ ...styles.td, textAlign: numeric ? "right" : "left" }}>
                    {numeric ? fmtCell(row[c.key]) : (row[c.key] === null || row[c.key] === undefined ? "" : String(row[c.key]))}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const CHART_H = 260
const MARGIN = { top: 12, right: 16, bottom: 56, left: 8 }
const FONT = 11

const useWidth = () => {
  const ref = useRef(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const update = () => setWidth(el.clientWidth)
    update()
    if (typeof ResizeObserver === "undefined") return undefined
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width]
}

const ChartKpi = ({ type, config, data }) => {
  const [ref, width] = useWidth()
  const yFields = (config.yFields || []).filter(Boolean)
  if (!config.xField || yFields.length === 0) {
    return <div style={styles.empty}>Select X and Y fields for this chart</div>
  }

  // Query values may arrive as formatted strings ("1,234.50"); scales need numbers.
  const points = data.map((row) => ({
    x: row[config.xField] === null || row[config.xField] === undefined ? "" : String(row[config.xField]),
    ys: yFields.map((f) => toNumber(row[f]) || 0),
  }))
  const colorFor = (i) => (i === 0 && config.color ? config.color : SERIES_COLORS[i % SERIES_COLORS.length])

  const maxY = Math.max(0, ...points.flatMap((p) => p.ys))
  const minY = Math.min(0, ...points.flatMap((p) => p.ys))
  const innerH = CHART_H - MARGIN.top - MARGIN.bottom
  const y = scaleLinear().domain([minY, maxY || 1]).nice().range([innerH, 0])
  const yTicks = y.ticks(5)
  // Size the left gutter to the widest tick label so numbers are never clipped.
  const left = MARGIN.left + Math.max(...yTicks.map((t) => compact(t).length)) * (FONT * 0.62) + 8

  const innerW = Math.max(width - left - MARGIN.right, 10)
  const x = scaleBand().domain(points.map((p, i) => String(i))).range([0, innerW]).padding(0.2)
  const bandW = x.bandwidth() / yFields.length
  // One x label per ~70px so category names don't pile up.
  const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(innerW / 70))))
  const maxChars = Math.max(6, Math.floor((innerW / Math.max(1, points.length / labelEvery)) / (FONT * 0.55)))

  const linePath = (si) =>
    points
      .map((p, i) => (i === 0 ? "M" : "L") + (x(String(i)) + x.bandwidth() / 2) + "," + y(p.ys[si]))
      .join(" ")

  return (
    <div ref={ref} style={{ width: "100%" }}>
      {width > 0 && (
        <svg width={width} height={CHART_H} style={{ display: "block" }}>
          <g transform={"translate(" + left + "," + MARGIN.top + ")"}>
            {yTicks.map((t) => (
              <g key={t} transform={"translate(0," + y(t) + ")"}>
                <line x2={innerW} stroke="#e5e7eb" strokeDasharray="3 3" />
                <text x={-8} dy="0.32em" textAnchor="end" fontSize={FONT} fill="#6b7280">{compact(t)}</text>
              </g>
            ))}

            {type === "bar" &&
              points.map((p, i) =>
                p.ys.map((val, si) => (
                  <rect
                    key={i + "-" + si}
                    x={x(String(i)) + si * bandW}
                    y={Math.min(y(val), y(0))}
                    width={bandW}
                    height={Math.abs(y(val) - y(0))}
                    fill={colorFor(si)}
                  >
                    <title>{p.x + " – " + yFields[si] + ": " + fmt(val)}</title>
                  </rect>
                )),
              )}

            {(type === "line" || type === "area") &&
              yFields.map((f, si) => (
                <g key={f}>
                  {type === "area" && (
                    <path
                      d={linePath(si) + "L" + (x(String(points.length - 1)) + x.bandwidth() / 2) + "," + y(0) + "L" + (x("0") + x.bandwidth() / 2) + "," + y(0) + "Z"}
                      fill={colorFor(si)}
                      fillOpacity={0.2}
                    />
                  )}
                  <path d={linePath(si)} fill="none" stroke={colorFor(si)} strokeWidth={2} />
                  {points.map((p, i) => (
                    <circle key={i} cx={x(String(i)) + x.bandwidth() / 2} cy={y(p.ys[si])} r={3.5} fill={colorFor(si)}>
                      <title>{p.x + " – " + f + ": " + fmt(p.ys[si])}</title>
                    </circle>
                  ))}
                </g>
              ))}

            <line y1={y(0)} y2={y(0)} x2={innerW} stroke="#9ca3af" />
            {points.map((p, i) =>
              i % labelEvery === 0 ? (
                <text
                  key={i}
                  transform={"translate(" + (x(String(i)) + x.bandwidth() / 2) + "," + (innerH + 14) + ") rotate(-30)"}
                  textAnchor="end"
                  fontSize={FONT}
                  fill="#6b7280"
                >
                  {p.x.length > maxChars ? p.x.slice(0, maxChars - 1) + "…" : p.x}
                </text>
              ) : null,
            )}
          </g>
        </svg>
      )}
      {yFields.length > 1 && (
        <div style={styles.legend}>
          {yFields.map((f, si) => (
            <span key={f} style={{ marginRight: 12 }}>
              <span style={{ ...styles.swatch, background: colorFor(si) }} />
              {prettyHeader(f)}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const PieKpi = ({ config, data }) => {
  const [ref, width] = useWidth()
  if (!config.labelField || !config.valueField) {
    return <div style={styles.empty}>Select label and value fields for this chart</div>
  }

  const slices = data
    .map((row) => ({
      label: row[config.labelField] === null || row[config.labelField] === undefined ? "" : String(row[config.labelField]),
      value: Math.max(toNumber(row[config.valueField]) || 0, 0),
    }))
    .filter((d) => d.value > 0)
  const total = slices.reduce((a, d) => a + d.value, 0)
  if (total === 0) return <div style={styles.empty}>No data</div>

  const size = Math.min(220, Math.max(width, 120))
  const radius = size / 2
  const arcs = d3Pie().sort(null).value((d) => d.value)(slices)
  const path = d3Arc().innerRadius(0).outerRadius(radius - 2)

  return (
    <div ref={ref} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
      {width > 0 && (
        <svg width={size} height={size} style={{ flex: "none" }}>
          <g transform={"translate(" + radius + "," + radius + ")"}>
            {arcs.map((a, i) => (
              <path key={i} d={path(a)} fill={SERIES_COLORS[i % SERIES_COLORS.length]} stroke="#fff" strokeWidth={1}>
                <title>{a.data.label + ": " + fmt(a.data.value) + " (" + ((a.data.value / total) * 100).toFixed(1) + "%)"}</title>
              </path>
            ))}
          </g>
        </svg>
      )}
      <div style={{ fontSize: 12, color: "#374151", minWidth: 0, flex: "1 1 140px" }}>
        {slices.map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
            <span style={{ ...styles.swatch, background: SERIES_COLORS[i % SERIES_COLORS.length], flex: "none" }} />
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.label}>{d.label}</span>
            <span style={{ marginLeft: 8, color: "#6b7280" }}>{((d.value / total) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

const KpiBody = ({ kpi, data }) => {
  const config = kpi.config || {}
  if (!data || data.length === 0) return <div style={styles.empty}>No data</div>
  switch (kpi.type) {
    case "badge":
      return <BadgeKpi config={config} data={data} />
    case "table":
      return <TableKpi config={config} data={data} />
    case "pie":
      return <PieKpi config={config} data={data} />
    case "bar":
    case "line":
    case "area":
      return <ChartKpi type={kpi.type} config={config} data={data} />
    default:
      return <div style={styles.empty}>Unsupported KPI type: {kpi.type}</div>
  }
}

// ── Dashboard ────────────────────────────────────────────────────────────────

const DashboardReport = ({ kpis = [], gridData = [] }) => {
  const getQueryData = (queryId) => {
    const match = gridData.find((d) => d.queryId === queryId)
    return (match && match.data) || []
  }

  if (kpis.length === 0) {
    return <div style={styles.empty}>This dashboard has no KPIs configured.</div>
  }

  return (
    <div style={styles.grid}>
      {kpis.map((kpi) => {
        const span = Math.min(Math.max(parseInt(kpi.width, 10) || 1, 1), 4)
        return (
          <div key={kpi.id} style={{ ...styles.card, gridColumn: `span ${span}` }}>
            {kpi.title && <div style={styles.title}>{kpi.title}</div>}
            <KpiBody kpi={kpi} data={getQueryData(kpi.queryId)} />
          </div>
        )
      })}
    </div>
  )
}

const styles = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    fontFamily: "Inter, sans-serif",
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: 16,
    minWidth: 0,
  },
  title: { fontSize: 13, fontWeight: 600, color: "#6b7280", marginBottom: 8 },
  badgeValue: { fontSize: 32, fontWeight: 700, lineHeight: 1.2 },
  empty: { color: "#9ca3af", fontSize: 13, padding: "8px 0" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e5e7eb", color: "#374151", fontWeight: 600, whiteSpace: "nowrap" },
  td: { padding: "6px 8px", borderBottom: "1px solid #f3f4f6" },
  legend: { fontSize: 12, color: "#374151", marginTop: 4 },
  swatch: { display: "inline-block", width: 10, height: 10, borderRadius: 2, marginRight: 4 },
}

export default DashboardReport
